import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tag, Typography } from 'antd';
import { CalendarOutlined, FieldTimeOutlined, UpSquareOutlined, WarningOutlined } from '@ant-design/icons';
import { useGetCountries } from 'api/countries/use-get-countries.ts';
import { addressStatus } from 'shared/utils/address-status.ts';
import { findCountriesById } from 'shared/utils/countries';
import { parseAddressExtra } from 'shared/utils/parse-address-extra.ts';
import { formatDate } from 'shared/utils/time/format-date.ts';
import styles from './cargo-address-box.module.scss';
import type { ICargoAddressBoxProps } from './types';

export const CargoAddressBox: React.FC<ICargoAddressBoxProps> = (props) => {
  const { address, customAddress, dateFrom, dateTo, customRequirements, actualDate } = props;
  const { t } = useTranslation();
  const { data: countries } = useGetCountries();

  const country = findCountriesById(address.countryId, countries);

  const { withLift, withBooking, withFixTime, withAttention } = parseAddressExtra(address.extraData);

  return (
    <div className={styles.box}>
      {dateFrom && dateTo ? (
        <Typography.Text strong>
          {formatDate(dateFrom)} — {formatDate(dateTo)}
        </Typography.Text>
      ) : null}
      {actualDate ? (
        <Typography.Text className={styles.actualDate}>
          {t('common:actual_date')}: {formatDate(actualDate)}
        </Typography.Text>
      ) : null}

      <Typography.Text className={styles.title}>{t('common:address')}:</Typography.Text>
      <Typography.Text className={styles.address} italic>
        {address.name}
      </Typography.Text>
      <Typography.Text className={styles.address}>
        <strong>{country?.name}</strong>, <strong>{address.postCode}</strong> {address.city}, {address.address}{' '}
        {addressStatus(address.googleLocationType, {
          withoutLike: true,
          detectionType: address.detectionType,
        })}
      </Typography.Text>
      <Typography.Text className={styles.coordinates}>
        <a
          href={`https://www.google.com/maps/place/${address.lat},${address.lng}`}
          target='_blank'
          rel='noopener noreferrer'>
          {address.lat}, {address.lng}
        </a>
      </Typography.Text>

      {address.referenceNumber ? (
        <Typography.Text className={styles.reference} strong>
          {t('cargo:reference_number')}: {address.referenceNumber}
        </Typography.Text>
      ) : null}

      {address.description ? (
        <>
          <Typography.Text className={styles.title}>{t('common:description')}:</Typography.Text>
          <div className={styles.description}>{address.description}</div>
        </>
      ) : null}

      <div className={styles.extraBox}>
        {withLift ? (
          <Tag icon={<UpSquareOutlined />} color='volcano'>
            {t('cargo:address_with_lift')}
          </Tag>
        ) : null}
        {withBooking ? (
          <Tag icon={<CalendarOutlined />} color='volcano'>
            {t('cargo:address_booking')}
          </Tag>
        ) : null}
        {withFixTime ? (
          <Tag icon={<FieldTimeOutlined />} color='volcano'>
            {t('cargo:address_fix_time')}
          </Tag>
        ) : null}
        {withAttention ? (
          <Tag icon={<WarningOutlined />} color='volcano'>
            {t('cargo:address_important_info')}
          </Tag>
        ) : null}
      </div>

      {customRequirements ? (
        <>
          <Typography.Text className={styles.title}>{t('cargo:custom')}:</Typography.Text>
          {customAddress ? (
            <>
              <Typography.Text className={styles.address}>
                <Typography.Text className={styles.address} italic>
                  {customAddress.name}
                </Typography.Text>{' '}
                <strong>{customAddress.postCode}</strong> {customAddress.city}, {customAddress.address}{' '}
                {addressStatus(customAddress.googleLocationType, {
                  withoutLike: true,
                  detectionType: customAddress.detectionType,
                })}
              </Typography.Text>
              <Typography.Text className={styles.coordinates}>
                <a
                  href={`https://www.google.com/maps/place/${customAddress.lat},${customAddress.lng}`}
                  target='_blank'
                  rel='noopener noreferrer'>
                  {customAddress.lat}, {customAddress.lng}
                </a>
              </Typography.Text>
              {customAddress.description ? (
                <>
                  <Typography.Text className={styles.title}>{t('common:custom_description')}:</Typography.Text>
                  <div className={styles.description}>{customAddress.description}</div>
                </>
              ) : null}
            </>
          ) : (
            t(`cargo:${customRequirements}`)
          )}
        </>
      ) : null}
    </div>
  );
};
